import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';

import DescriptionIcon from '@material-ui/icons/Description';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import Dialog from '~/components/Dialog';
import ImageZoomViewer from '~/components/ImageZoomViewer';
import Request from '~/providers/Request';

const typeIcon = (type) => {
  if (type === 'image') return <ImageIcon fontSize="small" />;
  if (type === 'pdf') return <PictureAsPdfIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
};

const formatSize = (bytes) => {
  const b = Number(bytes || 0);
  if (b <= 0) return '—';
  const mb = b / (1024 * 1024);
  // arquivos pequenos nao zeram (ex.: "< 0.01 MB")
  return mb < 0.01 ? '< 0.01 MB' : `${mb.toFixed(2)} MB`;
};

const previewTypeOf = (file) => {
  if (!file) return null;
  if (file.type === 'image' || file.extname === 'svg') return 'image';
  if (file.type === 'pdf') return 'pdf';
  return 'file';
};

const arrowStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 8,
  zIndex: 3,
  backgroundColor: 'rgba(60,64,67,0.85)',
  color: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
});

// Aba "Exames": lista os arquivos enviados pelo usuario (exames, documentos,
// etc.) e abre cada um num modal (imagem/PDF) via download autenticado.
const Exams = ({ idClient }) => {
  const classesTable = tableStyles();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [openIndex, setOpenIndex] = useState(-1);
  const [preview, setPreview] = useState({ url: null, loading: false });

  const isOpen = openIndex >= 0;
  const currentFile = isOpen ? files[openIndex] : null;
  const previewType = previewTypeOf(currentFile);
  const hasPrevious = openIndex > 0;
  const hasNext = openIndex >= 0 && openIndex < files.length - 1;

  async function getFiles() {
    setLoading(true);
    Request.get(`/admin/users/${idClient}/files`, {}, { normalizeJSON: false })
      .then((response) => {
        setFiles((response && response.data) || []);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  const goPrevious = useCallback(() => {
    setOpenIndex((index) => (index > 0 ? index - 1 : index));
  }, []);

  const goNext = useCallback(() => {
    setOpenIndex((index) => (index >= 0 ? index + 1 : index));
  }, []);

  function closePreview() {
    setOpenIndex(-1);
  }

  function handleDownload() {
    if (!preview.url || !currentFile) return;
    const link = document.createElement('a');
    link.href = preview.url;
    link.download = currentFile.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  useEffect(() => {
    if (idClient) getFiles();
  }, [idClient]);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    async function loadFile() {
      setPreview({ url: null, loading: true });
      const res = await Request.download(
        `/admin/files/${currentFile.uuid}/download`
      );
      if (!res) {
        if (!cancelled) setPreview({ url: null, loading: false });
        return;
      }
      const blob = await res.blob();
      objectUrl = window.URL.createObjectURL(blob);
      if (cancelled) return;
      setPreview({ url: objectUrl, loading: false });
    }

    if (currentFile) {
      loadFile();
    } else {
      setPreview({ url: null, loading: false });
    }

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [currentFile && currentFile.uuid]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'ArrowLeft' && hasPrevious) goPrevious();
      if (event.key === 'ArrowRight' && hasNext) goNext();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasPrevious, hasNext, goPrevious, goNext]);

  return (
    <Paper Icon={DescriptionIcon} iconColor="blue" title="Exames">
      <Typography variant="subtitle2" color="primary">
        <strong>Arquivos enviados: </strong>
        {files.length}
      </Typography>
      <Table
        emptyData={files.length === 0}
        loading={loading}
        columns={
          <>
            <TableCell className={classesTable.cell}>Arquivo</TableCell>
            <TableCell className={classesTable.cell}>Categoria</TableCell>
            <TableCell className={classesTable.cell}>Tipo</TableCell>
            <TableCell className={classesTable.cell}>Tamanho</TableCell>
            <TableCell className={classesTable.cell}>Data</TableCell>
            <TableCell className={classesTable.cell}>Ações</TableCell>
          </>
        }
      >
        {files.map((file, index) => (
          <TableRow key={file.uuid} hover className={classesTable.row}>
            <TableCell>
              <Box display="flex" alignItems="center">
                {typeIcon(file.type)}
                <span style={{ marginLeft: 6 }}>{file.name}</span>
              </Box>
            </TableCell>
            <TableCell>
              <Chip
                label={file.category}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </TableCell>
            <TableCell>{String(file.extname || '').toUpperCase()}</TableCell>
            <TableCell>{formatSize(file.size)}</TableCell>
            <TableCell>
              {file.createdAt
                ? moment(file.createdAt).format('DD/MM/YYYY HH:mm')
                : '-'}
            </TableCell>
            <TableCell>
              <Tooltip title="Visualizar">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setOpenIndex(index)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </Table>

      <Dialog
        isOpen={isOpen}
        onClose={closePreview}
        handleClose={closePreview}
        icon={<DescriptionIcon />}
        title={currentFile ? currentFile.name : ''}
        headerActions={
          <IconButton
            color="inherit"
            onClick={handleDownload}
            disabled={!preview.url}
            aria-label="Download"
          >
            <GetAppIcon />
          </IconButton>
        }
      >
        <Box position="relative" minHeight="72vh">
          {preview.loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="72vh"
            >
              <Typography>Carregando…</Typography>
            </Box>
          )}

          {!preview.loading && preview.url && previewType === 'image' && (
            <ImageZoomViewer src={preview.url} alt={currentFile.name} />
          )}

          {!preview.loading && preview.url && previewType === 'pdf' && (
            <iframe
              title={currentFile.name}
              src={preview.url}
              style={{ width: '100%', height: '75vh', border: 0 }}
            />
          )}

          {!preview.loading && preview.url && previewType === 'file' && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="72vh"
            >
              <InsertDriveFileIcon style={{ fontSize: 64, color: '#90a4ae' }} />
              <Typography variant="subtitle1" style={{ margin: '8px 0 16px' }}>
                {currentFile.name}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={handleDownload}
              >
                Baixar arquivo
              </Button>
            </Box>
          )}

          {!preview.loading && !preview.url && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="72vh"
            >
              <Typography color="error">
                Não foi possível carregar o arquivo.
              </Typography>
            </Box>
          )}

          <IconButton
            size="small"
            style={{ ...arrowStyle('left'), opacity: hasPrevious ? 1 : 0.25 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={goPrevious}
            disabled={!hasPrevious}
            aria-label="Anterior"
          >
            <NavigateBeforeIcon />
          </IconButton>

          <IconButton
            size="small"
            style={{ ...arrowStyle('right'), opacity: hasNext ? 1 : 0.25 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={goNext}
            disabled={!hasNext}
            aria-label="Próximo"
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default React.memo(Exams);
